import { getCsrfToken } from './auth';

/**
 * One place that talks to the quiz API.
 *
 * It exists so the CSRF header and the error-unwrapping are written once: four
 * screens each doing their own `fetch` is how one of them ends up posting without
 * the token, or showing "[object Object]" where FastAPI sent a perfectly good
 * sentence in `detail`.
 */

async function call(method, url, body) {
  const opts = { method, headers: {} };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  if (method !== 'GET') opts.headers['X-CSRF-Token'] = getCsrfToken();

  const res = await fetch(url, opts);
  let data = null;
  try { data = await res.json(); } catch (_) { /* a 500 can arrive as HTML */ }

  if (!res.ok) {
    // FastAPI puts the human-readable reason in `detail`, and these endpoints
    // deliberately write it as a sentence a person can act on ("3 people have
    // already started this quiz…"). Surface it verbatim.
    //
    // Some refusals send an OBJECT instead — "this document already has a quiz"
    // carries the quiz's id so the screen can offer a link rather than a dead end.
    // Without this branch that message renders as "[object Object]".
    const detail = data?.detail;
    const isObject = detail && typeof detail === 'object';
    const err = new Error(
      (isObject ? detail.message : detail) || `Request failed (${res.status})`
    );
    err.status = res.status;
    if (isObject) err.detail = detail;
    throw err;
  }
  return data;
}

export const quizApi = {
  list: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return call('GET', `/api/quizzes${q ? `?${q}` : ''}`);
  },
  // Paged + searched SERVER-side: a controlled library is unbounded, so the picker
  // asks for one page and the search goes with it (`show_blocked` is deliberately a
  // server filter too, or the page would be short by however many it hid).
  documents: (params = {}) => {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return call('GET', `/api/quizzes/documents${q ? `?${q}` : ''}`);
  },
  get: (id) => call('GET', `/api/quizzes/${id}`),
  create: (payload) => call('POST', '/api/quizzes', payload),
  update: (id, payload) => call('PUT', `/api/quizzes/${id}`, payload),
  saveQuestions: (id, questions) => call('PUT', `/api/quizzes/${id}/questions`, { questions }),
  publish: (id) => call('POST', `/api/quizzes/${id}/publish`),
  copyForward: (id, revision, title) =>
    call('POST', `/api/quizzes/${id}/copy-forward`, { revision, title }),
  remove: (id) => call('DELETE', `/api/quizzes/${id}`),

  // Write this revision's questions from the document's own file. A background job:
  // it downloads two files out of Orcanos and makes two model calls, which is far
  // too long for one request. `cancelGenerate` stops the WATCHING, not the run —
  // and it cannot un-download a file Orcanos has already logged (Critical Note #39).
  generate: (id, payload) => call('POST', `/api/quizzes/${id}/generate`, payload),
  generateStatus: (id, jobId) => call('GET', `/api/quizzes/${id}/generate/${jobId}`),
  cancelGenerate: (id, jobId) => call('DELETE', `/api/quizzes/${id}/generate/${jobId}`),
  // The rolled-up results: who owes it, who passed, and how each question performed.
  // `attempts` is the flat log behind it — kept apart because "what happened" and
  // "how is it going" are different questions and the second is derived, not raw.
  results: (id) => call('GET', `/api/quizzes/${id}/results`),
  attempts: (id) => call('GET', `/api/quizzes/${id}/attempts`),
  // One sitting's marked paper — what that person picked, beside the answer key.
  // Fetched on demand rather than folded into `results`: a quiz with 200 sittings
  // would otherwise ship every answer of every attempt to draw one table.
  attemptAnswers: (quizId, attemptId) =>
    call('GET', `/api/quizzes/${quizId}/attempts/${attemptId}/answers`),

  // `live` re-reads the trainee's own training tasks from Orcanos before answering
  // (one filter, a few seconds). Called as a SECOND request so the cached list can
  // render immediately and be corrected in place rather than made to wait.
  myTraining: (live = false) => call('GET', `/api/my/training${live ? '?live=1' : ''}`),
  startAttempt: (quizId) => call('POST', `/api/quizzes/${quizId}/attempts`),
  getAttempt: (attemptId) => call('GET', `/api/attempts/${attemptId}`),
  answer: (attemptId, questionId, optionIds) =>
    call('PUT', `/api/attempts/${attemptId}/answer`,
         { question_id: questionId, option_ids: optionIds }),
  submit: (attemptId) => call('POST', `/api/attempts/${attemptId}/submit`),
};

export default quizApi;
